import { AuthService } from './authService';
import {
  CompletePutAwayLineRequest,
  CompletePutAwayLineResponse,
  CompletePutAwayBulkRequest,
  CompletePutAwayBulkResponse,
  CreatePutAwayRequest,
  CreatePutAwayResponse,
  StcIntRelOrderDto,
  StcIntRelOrderLineDto,
  StcIntRelOrderDetailsDto,
  StcIntRelOrderLineWithHeaderDto,
  UpdateQuantityMovedRequest,
  UpdateStcIntRelOrderRequest,
  UpdateStcIntRelOrderStatusRequest,
  StcIntRelOrderStatus,
  NEW_PUTAWAY_ORDER_STATUSES,
  PUTAWAY_UNRESOLVED_BIN
} from '../types/putaway';

import { throwIfLockConflict } from '../utils/lockConflictError';
import { extractApiError } from '../utils/apiError';

// Re-eksport: disse typene var tidligere duplisert her (kilde: types/putaway.ts).
// Beholdes som eksport så eksisterende `import { ... } from 'services/putAwayService'` virker.
export type {
  PutAwayReservationDto,
  CreatePutAwayRequest,
  CreatePutAwayResponse,
  StcIntRelOrderDto,
  StcIntRelOrderLineDto,
  StcIntRelOrderDetailsDto,
  StcIntRelOrderLineWithHeaderDto,
  UpdateQuantityMovedRequest,
  UpdateStcIntRelOrderRequest,
  UpdateStcIntRelOrderStatusRequest,
  LockStcIntRelOrderRequest
} from '../types/putaway';

/** Valgfrie filtre for planleggerens ordrelister. Lagerfilter gjøres klientside (DTO har warehouseID). */
export interface PutAwayOrderListOptions {
  warehouseId?: string;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

/**
 * Response from running the standard put-away rule group over all open grunnlag
 * ("Opprett utlegg" tool). Mirrors RunPutAwayResponse in BizClick.WMS.Shared.
 */
export interface RunPutAwayResponse {
  success: boolean;
  putAwayNeeded: boolean;
  stcOrderNo: number | null;
  stcOrderNos: number[];
  processedCount: number;
  totalLineCount: number;
  ruleGroupId: number;
  message: string | null;
  errorMessage: string | null;
}

// Interfaces for batch put-away from relocation (Grunnlag)
export interface CreatePutAwayFromRelocationRequest {
  relocationIds: string[];
}

export interface CreatePutAwayFromRelocationResponse {
  success: boolean;
  stcOrderNo?: number;
  processedCount: number;
  failedCount: number;
  successfulRelIds: string[];
  failures: PutAwayFailure[];
  errorMessage?: string;
  totalLineCount: number;
}

// Respons fra sletting av utplasseringsordre (grunnlaget frigis tilbake til Grunnlag-skjermen)
export interface DeletePutAwayOrderResponse {
  message: string;
  releasedGrunnlagCount: number;
}

// Respons fra regenerering av utplasseringsordre fra grunnlag.
// success=false med deletedOrderNo satt betyr: ordren ble slettet, men regenereringen feilet —
// grunnlaget ligger da tilgjengelig i Grunnlag-skjermen for manuell generering.
export interface RegeneratePutAwayOrderResponse {
  success: boolean;
  deletedOrderNo: number;
  deletedLineCount: number;
  releasedGrunnlagCount: number;
  newOrderNo?: number;
  newLineCount: number;
  processedCount: number;
  failedCount: number;
  errorMessage?: string;
}

export interface PutAwayFailure {
  relId: string;
  itemNo: string;
  errorMessage: string;
}

class PutAwayService {

  // ---------------------------------------------------------------------------
  // Planlegger (lagersjef): lesing av plasseringsordre per status / periode
  // ---------------------------------------------------------------------------

  /**
   * Hent plasseringsordre med én gitt ordrestatus (StatusType 26), inkludert ordre som er
   * låst av operatører. Backend ekskluderer låste ordre som standard kun for status 10,
   * men planleggeren skal alltid se alt — derfor sendes excludeLocked=false eksplisitt.
   */
  async getPutAwayOrdersByStatus(status: number, options?: PutAwayOrderListOptions): Promise<StcIntRelOrderDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/intrelorders/status/${status}?excludeLocked=false`
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Kunne ikke hente plasseringsordre'));
    }

    const orders: StcIntRelOrderDto[] = await response.json();
    return this.applyWarehouseFilter(orders, options?.warehouseId);
  }

  /**
   * Hent plasseringsordre for flere statuser i ett kall (ett API-kall per status, parallelt).
   * Resultatet sorteres som backend: høyest prioritet først, deretter nyeste dato.
   */
  async getPutAwayOrdersByStatuses(statuses: readonly number[], options?: PutAwayOrderListOptions): Promise<StcIntRelOrderDto[]> {
    const perStatus = await Promise.all(statuses.map(s => this.getPutAwayOrdersByStatus(s, options)));
    return perStatus
      .flat()
      .sort((a, b) => b.orderPriority - a.orderPriority || b.transDt.localeCompare(a.transDt));
  }

  /** «Nye plasseringsordre»: status 0 (manuell) og 10 (systemgenerert, ikke godkjent). */
  async getNewPutAwayOrders(options?: PutAwayOrderListOptions): Promise<StcIntRelOrderDto[]> {
    return this.getPutAwayOrdersByStatuses(NEW_PUTAWAY_ORDER_STATUSES, options);
  }

  /** «Plasseringsordre under arbeid»: status 20 (godkjent, synlig for operatør). */
  async getWorkingPutAwayOrders(options?: PutAwayOrderListOptions): Promise<StcIntRelOrderDto[]> {
    return this.getPutAwayOrdersByStatus(StcIntRelOrderStatus.GODKJENT, options);
  }

  /**
   * «Avsluttede plasseringsordre»: ordre i status 30 med TransDt i perioden.
   * Backend-endepunktet returnerer alle statuser i perioden, så status filtreres her.
   * Standard periode er siste 7 dager (som «Avsluttede plukklister»).
   */
  async getCompletedPutAwayOrders(
    dateFrom?: Date,
    dateTo?: Date,
    options?: PutAwayOrderListOptions
  ): Promise<StcIntRelOrderDto[]> {
    const defaultFrom = new Date();
    defaultFrom.setDate(defaultFrom.getDate() - 7);

    const params = new URLSearchParams();
    params.append('fromDate', (dateFrom || defaultFrom).toISOString());
    params.append('toDate', (dateTo || new Date()).toISOString());

    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/intrelorders/daterange?${params.toString()}`
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Kunne ikke hente avsluttede plasseringsordre'));
    }

    const orders: StcIntRelOrderDto[] = await response.json();
    return this.applyWarehouseFilter(
      orders.filter(o => o.orderStatus === StcIntRelOrderStatus.AVSLUTTET),
      options?.warehouseId
    );
  }

  /**
   * «Ordrelinjer»: alle linjer på åpne plasseringsordre (status 0, 10 og 20) med ordrehodet
   * vedlagt. Det finnes ikke et flatt linje-endepunkt i backend, så detaljene hentes per
   * ordre og slås sammen her. Fungerer fint for titalls åpne ordre; blir det hundrevis må
   * det lages et eget endepunkt.
   */
  async getOpenPutAwayOrderLines(options?: PutAwayOrderListOptions): Promise<StcIntRelOrderLineWithHeaderDto[]> {
    const orders = await this.getPutAwayOrdersByStatuses(
      [...NEW_PUTAWAY_ORDER_STATUSES, StcIntRelOrderStatus.GODKJENT],
      options
    );

    const details = await Promise.all(orders.map(o => this.getPutAwayOrderDetails(o.stcOrderNo)));

    return details.flatMap(d => d.lines.map(line => ({ ...line, header: d.header })));
  }

  /** Sann hvis linjen mangler mål-lokasjon (regelmotorens [X]-plassholder). */
  isUnresolvedLine(line: Pick<StcIntRelOrderLineDto, 'toBinCode'>): boolean {
    return (line.toBinCode || '').trim() === PUTAWAY_UNRESOLVED_BIN;
  }

  /**
   * Klientside lagerfilter. Ordre uten lager på hodet (tom WarehouseID) beholdes alltid —
   * de er ikke knyttet til et annet lager, og ville ellers blitt usynlige i planleggeren.
   */
  private applyWarehouseFilter(orders: StcIntRelOrderDto[], warehouseId?: string): StcIntRelOrderDto[] {
    if (!warehouseId) return orders;
    return orders.filter(o => !o.warehouseID?.trim() || o.warehouseID === warehouseId);
  }

  // ---------------------------------------------------------------------------
  // Planlegger (lagersjef): styring av plasseringsordre
  // ---------------------------------------------------------------------------

  /**
   * Godkjenn en plasseringsordre: ren statusovergang 0/10 → 20, ingen regelkjøring.
   * Speiler sperren i regelmotorens auto-godkjenning: ordren må ha minst én linje med en
   * reell mål-lokasjon (ikke [X]). Linjene kan sendes inn hvis de allerede er lastet;
   * ellers hentes de.
   */
  async approvePutAwayOrder(order: StcIntRelOrderDto, lines?: StcIntRelOrderLineDto[]): Promise<void> {
    if (!NEW_PUTAWAY_ORDER_STATUSES.includes(order.orderStatus)) {
      throw new Error(`Plasseringsordre #${order.stcOrderNo} har status ${order.orderStatus} og kan ikke godkjennes.`);
    }

    const orderLines = lines ?? (await this.getPutAwayOrderDetails(order.stcOrderNo)).lines;
    if (orderLines.length === 0) {
      throw new Error(`Plasseringsordre #${order.stcOrderNo} har ingen linjer og kan ikke godkjennes.`);
    }
    if (orderLines.every(l => this.isUnresolvedLine(l))) {
      throw new Error(
        `Plasseringsordre #${order.stcOrderNo} har ingen linjer med mål-lokasjon ([X]). Velg lokasjon på minst én linje før godkjenning.`
      );
    }

    await this.updatePutAwayOrderStatus(order.stcOrderNo, StcIntRelOrderStatus.GODKJENT);
  }

  /**
   * Endre prioritet på en plasseringsordre. Backend har bare full oppdatering av ordrehodet
   * (PUT intrelorders/{no}) som overskriver alle felt, så eksisterende verdier sendes tilbake
   * uendret. Lås (LockedBy/LockStatus) er ikke en del av request-bodyen og røres ikke.
   */
  async updatePutAwayOrderPriority(order: StcIntRelOrderDto, orderPriority: number): Promise<StcIntRelOrderDto> {
    const request: UpdateStcIntRelOrderRequest = {
      warehouseID: order.warehouseID,
      stcOrderNo: order.stcOrderNo,
      transDt: order.transDt,
      orderStatus: order.orderStatus,
      transType: order.transType,
      operatorID: order.operatorID,
      operatorGroupID: order.operatorGroupID,
      orderPriority,
      description1: order.description1,
      sessionID: order.sessionID,
      externalSystemID: order.externalSystemID != null ? Number(order.externalSystemID) : undefined,
    };

    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/intrelorders/${order.stcOrderNo}`,
      request
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Kunne ikke endre prioritet på plasseringsordre'));
    }

    return await response.json();
  }

  /**
   * Get put-away order details with all lines
   */
  async getPutAwayOrderDetails(stcOrderNo: number): Promise<StcIntRelOrderDetailsDto> {
    try {
      const response = await AuthService.authenticatedGet(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/details`
      );
      
      if (!response.ok) {
        throw new Error(`Failed to get put-away order details: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error getting put-away order details:', error);
      throw error;
    }
  }

  /**
   * Lock a put-away order to current operator
   */
  async lockPutAwayOrder(stcOrderNo: number, operatorID?: string): Promise<void> {
    const body = operatorID ? { operatorID } : {};

    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/intrelorders/${stcOrderNo}/lock`,
      body
    );

    if (!response.ok) {
      await throwIfLockConflict(response);
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Failed to lock put-away order'));
    }
  }

  /**
   * Unlock a put-away order
   */
  async unlockPutAwayOrder(stcOrderNo: number, operatorID?: string): Promise<void> {
    try {
      const body = operatorID ? { operatorID } : {};
      
      const response = await AuthService.authenticatedPost(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/unlock`,
        body
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(extractApiError(errorData, response, 'Failed to unlock put-away order'));
      }
    } catch (error) {
      console.error('Error unlocking put-away order:', error);
      throw error;
    }
  }

  /**
   * Delete a put-away order (manager/administrator only).
   * Grunnlag records are released back to the Grunnlag screen (LineStatus 30 -> 20).
   */
  async deletePutAwayOrder(stcOrderNo: number): Promise<DeletePutAwayOrderResponse> {
    const response = await AuthService.authenticatedDelete(
      `${API_BASE_URL}/intrelorders/${stcOrderNo}`
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Failed to delete put-away order'));
    }

    return await response.json();
  }

  /**
   * Delete and regenerate a put-away order from its grunnlag via the rule engine
   * (manager/administrator only). Used after grunndata changes (e.g. bin capacity)
   * so the order gets a new bin split.
   */
  async regeneratePutAwayOrder(stcOrderNo: number): Promise<RegeneratePutAwayOrderResponse> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/putaway/orders/${stcOrderNo}/regenerate`,
      {}
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Failed to regenerate put-away order'));
    }

    return await response.json();
  }

  /**
   * Update quantity moved for a specific put-away line
   */
  async updateQuantityMoved(stcOrderNo: number, stcLineNo: number, quantityMoved: number, movedBy?: string): Promise<void> {
    try {
      const request: UpdateQuantityMovedRequest = {
        quantityMoved,
        movedBy
      };

      const response = await AuthService.authenticatedPut(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/lines/${stcLineNo}/quantity`,
        request
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(extractApiError(errorData, response, 'Failed to update quantity moved'));
      }
    } catch (error) {
      console.error('Error updating quantity moved:', error);
      throw error;
    }
  }

  /**
   * Update put-away order status
   */
  async updatePutAwayOrderStatus(stcOrderNo: number, status: number): Promise<void> {
    try {
      const request: UpdateStcIntRelOrderStatusRequest = { status };

      const response = await AuthService.authenticatedPut(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/status`,
        request
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(extractApiError(errorData, response, 'Failed to update put-away order status'));
      }
    } catch (error) {
      console.error('Error updating put-away order status:', error);
      throw error;
    }
  }

  /**
   * Update put-away order line (for updating bin codes, etc.)
   */
  async updatePutAwayOrderLine(stcOrderNo: number, stcLineNo: number, updates: Partial<StcIntRelOrderLineDto>): Promise<StcIntRelOrderLineDto> {
    try {
      const response = await AuthService.authenticatedPut(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/lines/${stcLineNo}`,
        updates
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(extractApiError(errorData, response, 'Failed to update put-away order line'));
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating put-away order line:', error);
      throw error;
    }
  }

  /**
   * Update only the destination bin (ToBinCode) for a put-away line.
   * Used after capacity registration to re-suggest a new location for the rest-line.
   */
  async updateLineDestination(stcOrderNo: number, stcLineNo: number, toBinCode: string): Promise<void> {
    const response = await AuthService.authenticatedFetch(
      `${API_BASE_URL}/intrelorders/${stcOrderNo}/lines/${stcLineNo}/destination`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ toBinCode }),
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Failed to update line destination'));
    }
  }

  /**
   * Start put-away process (similar to start picking)
   */
  async startPutAway(stcOrderNo: number): Promise<void> {
    try {
      // Update status to "Under arbeid" (20) - similar to picking
      await this.updatePutAwayOrderStatus(stcOrderNo, 20);
    } catch (error) {
      console.error('Error starting put-away:', error);
      throw error;
    }
  }

  /**
   * Complete put-away process (Fullfør utplassering)
   * Sets all line statuses to 30 (Avsluttet) and OrderStatus to 30 (Avsluttet)
   */
  async completePutAway(stcOrderNo: number): Promise<void> {
    try {
      // Call backend endpoint that handles full completion:
      // - Updates all lines to LineStatus = 30 (Avsluttet)
      // - Updates order to OrderStatus = 30 (Avsluttet)
      const response = await AuthService.authenticatedPost(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/finalize`,
        {}
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(extractApiError(errorData, response, 'Failed to complete put-away'));
      }
    } catch (error) {
      console.error('Error completing put-away:', error);
      throw error;
    }
  }

  /**
   * Complete a single put-away line with bin location and quantity
   * This updates both the line and ItemBins inventory via domain events
   * Also creates StcTransaction records for inventory tracking
   */
  async completePutAwayLineWithBin(
    stcOrderNo: number,
    stcLineNo: number,
    warehouseID: string,
    toBinCode: string,
    quantity: number
  ): Promise<CompletePutAwayLineResponse> {
    try {
      const request: CompletePutAwayLineRequest = {
        warehouseID,
        toBinCode,
        quantity
      };

      const response = await AuthService.authenticatedPost(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/lines/${stcLineNo}/complete-with-bin`,
        request
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(extractApiError(errorData, response, 'Failed to complete put-away line'));
      }

      return await response.json();
    } catch (error) {
      console.error('Error completing put-away line with bin:', error);
      throw error;
    }
  }

  /**
   * Complete all pending lines in a put-away order to the same bin location
   * Bulk operation that updates ItemBins for all items via domain events
   * Also creates StcTransaction records for each line
   */
  async completePutAwayBulkToBin(
    stcOrderNo: number,
    warehouseID: string,
    toBinCode: string
  ): Promise<CompletePutAwayBulkResponse> {
    try {
      const request: CompletePutAwayBulkRequest = {
        warehouseID,
        toBinCode
      };

      const response = await AuthService.authenticatedPost(
        `${API_BASE_URL}/intrelorders/${stcOrderNo}/complete-all-to-bin`,
        request
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(extractApiError(errorData, response, 'Failed to complete bulk put-away'));
      }

      return await response.json();
    } catch (error) {
      console.error('Error completing bulk put-away to bin:', error);
      throw error;
    }
  }

  /**
   * Get available put-away orders for operator (locked to current operator or available)
   */
  /** warehouseId = valgt lager (utelatt = alle lager). Låst profil får alltid standardlageret i backend. */
  async getAvailablePutAwayOrdersForOperator(warehouseId?: string): Promise<StcIntRelOrderDto[]> {
    try {
      const query = warehouseId ? `?warehouseId=${encodeURIComponent(warehouseId)}` : '';
      const response = await AuthService.authenticatedGet(
        `${API_BASE_URL}/intrelorders/available${query}`
      );
      
      if (!response.ok) {
        throw new Error(`Failed to get available put-away orders for operator: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error getting available put-away orders for operator:', error);
      throw error;
    }
  }

  /**
   * Run the standard put-away rule group (SystemSetup.StdPutAwayRuleGroupID) over ALL
   * open grunnlag — the "Opprett utlegg" tool. Manager/administrator only.
   */
  async runPutAway(): Promise<RunPutAwayResponse> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/putaway/run`,
      {}
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      // 422/404 carry a RunPutAwayResponse body with errorMessage — surface it
      if (errorData && typeof errorData.success === 'boolean') {
        return errorData as RunPutAwayResponse;
      }
      throw new Error(extractApiError(errorData, response, 'Kunne ikke opprette utlegg'));
    }

    return await response.json();
  }

  /**
   * Create put-away orders from StcIntRelocation records (Grunnlag)
   * This implements the "Opprett utlegg på grunnlag" functionality
   */
  async createPutAwayFromRelocationBatch(relocationIds: string[]): Promise<CreatePutAwayFromRelocationResponse> {
    try {
      const request: CreatePutAwayFromRelocationRequest = {
        relocationIds
      };

      const response = await AuthService.authenticatedPost(
        `${API_BASE_URL}/putaway/create-from-relocation-batch`,
        request
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(extractApiError(errorData, response, 'Failed to create put-away from relocation'));
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating put-away from relocation batch:', error);
      throw error;
    }
  }

  // EXISTING LEGACY PUT-AWAY FUNCTIONALITY - Keep for backwards compatibility
  /**
   * Kjører put-away logikk for en StcTrans linje (Legacy functionality)
   */
  async createPutAwayForStcIntRelOrderLine(request: CreatePutAwayRequest): Promise<CreatePutAwayResponse> {
    try {
      const response = await AuthService.authenticatedPost(
        `${API_BASE_URL}/putaway/create-for-stc-int-rel-order-line`,
        request
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(extractApiError(errorData, response, 'Failed to create put-away'));
      }
      
      return await response.json();
    } catch (error) {
      console.error('Detaljert feil ved opprettelse av put-away:', error);
      console.error('Request data:', request);
      throw error;
    }
  }

  /**
   * Hjelpefunksjon for å bygge put-away request fra StcTrans data (Legacy functionality)
   * Now includes pOrderNo and pOrderLineNo for Standard mode to lookup StdLocationName
   */
  buildPutAwayRequest(
    stcTrans: any,
    companyNo: string,
    warehouseID: string,
    binGrpNo: number = 1,
    pickRuleNo: number = 1,
    putAwayToPickableBinYesNo: boolean = true
  ): CreatePutAwayRequest {
    return {
      companyNo,
      warehouseID,
      itemNo: stcTrans.itemNo,
      binGrpNo,
      pickRuleNo,
      grShipmentNo: stcTrans.grShipmentNo || 0,
      putAwayToPickableBinYesNo,
      quantity: stcTrans.quantity || 0,
      // For Standard mode - needed to lookup POrderLine.StdLocationName from ERP
      pOrderNo: stcTrans.orderNo || undefined,
      pOrderLineNo: stcTrans.lineNo || undefined
    };
  }
}

export const putAwayService = new PutAwayService();
